// @file Surface share panel store
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import PromiseQueue from '@@/bits/promise_queue'
import { SnackbarNotificationType, WallAccessPrivacyType, WallAccessRight } from '@@/enums'
import type { CanSetWallPrivacyOption, CanSetWallPrivacyOptions, WallAccessSettings } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { useGlobalSnackbarStore } from 'pinia/global_snackbar'
import PadletApi from 'surface/padlet_api'
import { computed, ref } from 'vue'

export const useSurfaceSharePanelStore = defineStore('surfaceSharePanel', () => {
  const surfaceVuexStore = getVuexStore<RootState>() // For gradual conversion to pinia
  const globalSnackbarStore = useGlobalSnackbarStore()
  const queue = new PromiseQueue()

  // Text
  const visitorRightText = {
    [WallAccessRight.None]: {
      title: __('No access'),
      description: __('Only collaborators can access this padlet.'),
    },
    [WallAccessRight.Read]: {
      title: __('Reader'),
      description: __('Visitors can read posts, write comments, and add reactions.'),
    },
    [WallAccessRight.Write]: {
      title: __('Writer'),
      description: __('Visitors can write new posts.'),
    },
    [WallAccessRight.Edit]: {
      title: __('Moderator'),
      description: __('Visitors can write, edit, and approve posts.'),
    },
  }

  // State
  const xSurfaceSharePanel = ref(false)
  const isAwaitingSharePanelApiResponse = ref(false)
  const privacyType = ref(WallAccessPrivacyType.Public)
  const canSetWallPrivacyOptions = ref<CanSetWallPrivacyOption[]>([])
  const visitorRight = ref(WallAccessRight.None)
  const password = ref<string | null>(null)

  // Getters
  const surfaceExportsScreenshotStatusPath = computed<string>(
    () => surfaceVuexStore?.state.constants.surfaceExportsScreenshotStatusPath,
  )
  const surfaceExportsDocumentStatusPath = computed<string>(
    () => surfaceVuexStore?.state.constants.surfaceExportsDocumentStatusPath,
  )
  const xSurfaceSharePanelCollaboratorsSection = computed<boolean>(
    () => surfaceVuexStore?.getters?.canIAdminister ?? false,
  )
  const allowedVisitorRights = computed(() => {
    const conditionalRights = {}
    if (canSetPrivacyOption(WallAccessPrivacyType.Private)) {
      conditionalRights[WallAccessRight.None] = visitorRightText[WallAccessRight.None]
    }

    const alwaysAllowedRights = {
      [WallAccessRight.Read]: visitorRightText[WallAccessRight.Read],
      [WallAccessRight.Write]: visitorRightText[WallAccessRight.Write],
      [WallAccessRight.Edit]: visitorRightText[WallAccessRight.Edit],
    }

    return { ...conditionalRights, ...alwaysAllowedRights }
  })
  const visitorRightName = computed(() => {
    return visitorRightText[visitorRight.value].title
  })

  // Actions
  async function fetchWallAccessSettings(): Promise<void> {
    try {
      const response = await PadletApi.WallAccessSettings.fetch(surfaceVuexStore?.getters?.wallId)
      const accessSettings = (response.data as JsonAPIResource<WallAccessSettings>).attributes
      privacyType.value = accessSettings.privacyType
      visitorRight.value = accessSettings.right
      password.value = accessSettings.password
    } catch (e) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Oops! Something went wrong.'),
      })
      captureFetchException(e, { source: 'SurfaceSharePanelFetchWallAccessSettings' })
    }
  }

  async function fetchWallPrivacyOptions(): Promise<void> {
    try {
      const response = await PadletApi.WallPrivacyOptions.fetchWallPrivacyOptions(surfaceVuexStore?.getters?.wallId)
      canSetWallPrivacyOptions.value = (
        response.data as JsonAPIResource<CanSetWallPrivacyOptions>
      ).attributes.privacyOptions.filter((privacyOption) => privacyOption.canSet)
    } catch (e) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Oops! Something went wrong.'),
      })
      captureFetchException(e, { source: 'SurfaceSharePanelFetchWallPrivacyOptions' })
    }
  }

  async function initialize(): Promise<void> {
    isAwaitingSharePanelApiResponse.value = true
    if (surfaceVuexStore?.getters?.canIAdminister as boolean) {
      await Promise.all([fetchWallAccessSettings(), fetchWallPrivacyOptions()])
    }

    isAwaitingSharePanelApiResponse.value = false
  }

  async function updateVisitorRight(
    oldRight: WallAccessRight,
    newRight: WallAccessRight,
    oldPrivacyType: WallAccessPrivacyType,
    newPrivacyType: WallAccessPrivacyType,
  ): Promise<void> {
    try {
      const response = await PadletApi.WallAccessSettings.update(surfaceVuexStore?.getters?.wallId, {
        privacyType: newPrivacyType,
        right: newRight,
        password: password.value,
      })
      const accessSettings = (response.data as JsonAPIResource<WallAccessSettings>).attributes
      visitorRight.value = accessSettings.right
      privacyType.value = accessSettings.privacyType
    } catch (e) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Sorry, your request could not be completed. Please try again.'),
      })
      captureFetchException(e, { source: 'SurfaceSharePanelUpdateVisitorRight' })
      visitorRight.value = oldRight
      privacyType.value = oldPrivacyType
    }
  }

  async function queueUpdateVisitorRight(newRight: WallAccessRight): Promise<void> {
    const oldRight = visitorRight.value
    if (newRight === oldRight) return

    // Update the visitor right to the new value on the frontend immediately, even before queueing the update request to the backend, for faster response
    visitorRight.value = newRight

    const oldPrivacyType = privacyType.value
    let newPrivacyType = oldPrivacyType

    if (newRight === WallAccessRight.None) {
      // If changing right to None, change privacy to Private, because only Private wall can have None right
      newPrivacyType = WallAccessPrivacyType.Private
    } else if (oldRight === WallAccessRight.None) {
      // If changing right from None to non-None, change privacy to Secret, because Private wall cannot have non-None right
      newPrivacyType = WallAccessPrivacyType.Secret
    }

    privacyType.value = newPrivacyType

    void queue.enqueue(
      'updateVisitorRight',
      async () => await updateVisitorRight(oldRight, newRight, oldPrivacyType, newPrivacyType),
    ) // Pass oldRight and oldPrivacyType so we can roll-back to oldRight and oldPrivacyType if the update request fails
  }

  function canSetPrivacyOption(privacyType: WallAccessPrivacyType): boolean {
    return (
      canSetWallPrivacyOptions.value.find(
        (option: CanSetWallPrivacyOption) => (option.name as string) === (privacyType as string),
      ) !== undefined
    )
  }

  return {
    // State
    xSurfaceSharePanel,
    isAwaitingSharePanelApiResponse,
    privacyType,
    visitorRight,

    // Getters
    surfaceExportsScreenshotStatusPath,
    surfaceExportsDocumentStatusPath,
    xSurfaceSharePanelCollaboratorsSection,
    allowedVisitorRights,
    visitorRightName,

    // Actions
    initialize,
    queueUpdateVisitorRight,
  }
})
